<!-- Una tabla por estado: monto, % vertical y variación contra el año anterior, por ejercicio. -->
<ng-template #statementTable let-lines let-title="title" let-base="base" let-test="test">
  <div class="alg-block">
    <div class="alg-sec">
      <h3>{{ title }}</h3>
      <span class="alg-aside">% sobre {{ base }}</span>
    </div>
    <div class="alg-table-wrap">
      <table class="alg-table statements-table" [attr.data-test]="test">
        <caption class="p-hidden-accessible">
          {{
            title + ' por ejercicio: monto, porcentaje sobre ' + base + ' y variación contra el año anterior'
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col" rowspan="2" class="concept-col">Concepto</th>
            <th
              scope="colgroup"
              class="year-col"
              *ngFor="let year of statements!.years; let i = index; trackBy: trackByIndex"
              [attr.colspan]="hasDelta(i) ? 3 : 2"
            >
              {{ year }}
            </th>
          </tr>
          <tr>
            <ng-container *ngFor="let year of statements!.years; let i = index; trackBy: trackByIndex">
              <th scope="col" class="is-num">Monto</th>
              <th scope="col" class="is-num">%</th>
              <th scope="col" class="is-num" *ngIf="hasDelta(i)">Var.</th>
            </ng-container>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let line of lines; trackBy: trackByIndex" [class]="'level-' + line.depth" data-test="statement-row">
            <th scope="row" class="concept">
              <button
                *ngIf="line.expandable"
                type="button"
                class="alg-exp"
                data-test="statement-toggle"
                [attr.aria-expanded]="line.expanded"
                [attr.aria-label]="(line.expanded ? 'Cerrar el detalle de ' : 'Abrir el detalle de ') + line.row.label"
                (click)="toggle(line)"
              >
                <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </button>
              <span>{{ line.row.label }}</span>
            </th>
            <ng-container *ngFor="let year of statements!.years; let i = index; trackBy: trackByIndex">
              <td class="is-num" [class.is-bad]="(line.row.values[year] ?? 0) < 0">{{ amount(line.row.values[year]) }}</td>
              <td class="is-num is-muted">{{ percent(line.row.verticalPct[year]) }}</td>
              <td class="is-num is-muted" *ngIf="hasDelta(i)">{{ rowDelta(line.row, i) }}</td>
            </ng-container>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</ng-template>

<section class="snapshot-tab" aria-label="Estados financieros">
  <app-snapshot-state *ngIf="envelopeState; else withResult" [state]="envelopeState" (retry)="retry()"></app-snapshot-state>

  <ng-template #withResult>
    <article class="alg-card">
      <header class="alg-card__header">
        <span class="alg-card__icon"><i class="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i></span>
        <div class="alg-card__titles">
          <h2>Estados financieros</h2>
          <p data-test="statements-source">
            Declaración anual del SAT (formato 2022)<ng-container *ngIf="view?.snapshot?.viewedRevision">
              · foto revisión {{ view?.snapshot?.viewedRevision }}</ng-container
            >
          </p>
        </div>
        <span class="alg-pill is-info" *ngIf="statements?.effectiveYear as year" data-test="statements-effective"
          >La calculadora usa {{ year }}</span
        >
      </header>

      <ng-container *ngIf="statements; else noStatements">
        <ng-container *ngFor="let year of statements.years; trackBy: trackByIndex">
          <p class="alg-notice is-neutral" *ngIf="statements.flags[year]?.length" data-test="statements-flags">
            <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            <span
              >{{ year }}:
              <ng-container *ngFor="let flag of statements.flags[year]; let last = last; trackBy: trackByIndex"
                >{{ flagLabel(flag) }}<ng-container *ngIf="!last"> · </ng-container></ng-container
              ></span
            >
          </p>
        </ng-container>
        <p class="alg-notice is-neutral" *ngIf="statements.years.length === 1" data-test="statements-single-year">
          <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          <span
            >Solo hay un ejercicio: sin variación contra el año anterior ni depreciación del año (el EBITDA es la utilidad de
            operación).</span
          >
        </p>

        <div class="alg-card__body">
          <ng-container
            *ngTemplateOutlet="
              statementTable;
              context: { $implicit: income, title: 'Estado de resultados', base: 'Ingresos Netos', test: 'income-table' }
            "
          ></ng-container>
          <ng-container
            *ngTemplateOutlet="
              statementTable;
              context: { $implicit: balance, title: 'Balance general', base: 'Activo total', test: 'balance-table' }
            "
          ></ng-container>

          <!-- Check del Excel: Activo = Pasivo + Capital, por ejercicio. -->
          <div class="alg-strip check-strip" data-test="statements-check">
            <div class="alg-strip__cell">
              <div class="alg-strip__title">Check</div>
              <div class="alg-strip__hint">Activo = Pasivo + Capital</div>
            </div>
            <div class="alg-strip__cell" *ngFor="let year of statements.years; trackBy: trackByIndex" [attr.data-test]="'check-' + year">
              <div class="alg-strip__label">{{ year }}</div>
              <ng-container *ngIf="checkOf(year) as check; else noCheck">
                <div class="alg-strip__value" [class.is-ok]="check.ok" [class.is-bad]="!check.ok">
                  <i class="fa-solid" [ngClass]="check.ok ? 'fa-circle-check' : 'fa-circle-xmark'" aria-hidden="true"></i>
                  {{ check.ok ? 'Cuadra' : 'No cuadra' }}
                </div>
                <div class="alg-strip__hint" *ngIf="!check.ok">
                  {{ amount(check.assets) }} contra {{ amount(check.liabilitiesPlusEquity) }}
                </div>
              </ng-container>
              <ng-template #noCheck><div class="alg-strip__value">—</div></ng-template>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #noStatements>
        <div class="alg-card__body">
          <div class="empty-note" data-test="statements-empty">
            <i class="fa-solid fa-circle-minus" aria-hidden="true"></i>
            <span *ngIf="isPersonaFisica; else noDeclaration"
              >Persona física: el SAT no entrega estados financieros en la declaración anual de una persona física.</span
            >
            <ng-template #noDeclaration
              ><span>La foto no trae declaración anual con estados financieros. La calculadora mide sin ellos.</span></ng-template
            >
          </div>
        </div>
      </ng-template>
    </article>
  </ng-template>
</section>
